/**
 * Microphone permission helper for Twilio browser calling.
 *
 * - Never prompts on page load — only when an outbound/inbound call starts.
 * - Uses Permissions API when available to avoid re-prompting.
 * - Releases the temporary getUserMedia stream immediately so the mic
 *   indicator is not left on; Twilio acquires audio when it connects.
 */

let microphonePermissionGranted = false;

async function queryMicrophonePermissionState() {
  if (!navigator.permissions?.query) {
    return null;
  }

  try {
    const status = await navigator.permissions.query({ name: 'microphone' });
    return status.state;
  } catch (error) {
    // Firefox and some browsers reject { name: 'microphone' }
    return null;
  }
}

/**
 * Ensures the user has granted microphone access for Twilio Voice.
 * @returns {Promise<boolean>} true if microphone access is available
 */
export async function ensureMicrophonePermission() {
  if (microphonePermissionGranted) {
    return true;
  }

  const permissionState = await queryMicrophonePermissionState();
  if (permissionState === 'granted') {
    microphonePermissionGranted = true;
    return true;
  }
  if (permissionState === 'denied') {
    return false;
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return false;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    microphonePermissionGranted = true;
    return true;
  } catch (error) {
    return false;
  }
}
